import { useCallback, useRef, useState } from 'react'
import { SmsConversationBot } from '@numeo/types'
import { useSnackbar } from '@/contexts/SnackbarContext'
import { SnackbarType } from '@/utils/snackbar'
import { getUserErrorMessage } from '@/utils/get-user-error-message'
import { setConversationBot } from '../services/boxTruckLoadsApi'
import { trackBoxTruckOfferBotToggled } from '../utils/analytics'

/**
 * The load bot in ONE conversation: its state, who decided it, and the one person who may undo that.
 *
 * Shared because the switch is drawn in two places, and written twice they had already drifted — two
 * spellings of "paused", two stale-answer guards, two error paths (P6).
 */
interface ConversationBot {
    /** The server's answer, or null while it has not given one — draw nothing until it has. */
    bot: SmsConversationBot | null
    /** Whose decision the silence is, when the bot is off. Null when it is on. */
    off: SmsConversationBot['off']
    /** This operator may flip it: it is on, or they are the one who paused it. */
    mine: boolean
    /** What the switch says beside the box. */
    label: string
    /** Why, in a sentence, for the control's title. */
    hint: string
    saving: boolean
    toggle: (on: boolean) => Promise<void>
    /**
     * For a surface handed the state with its thread rather than fetching it.
     *
     * `forDriver` is the conversation the answer is ABOUT — pass it for anything fetched asynchronously, so
     * a read still in flight cannot draw the previous driver's state under the new one's name.
     */
    adopt: (bot: SmsConversationBot | null | undefined, forDriver?: string) => void
}

const LABELS: Record<NonNullable<SmsConversationBot['off']> | 'on', string> = {
    on: 'Bot active',
    tenant: 'Bot off',
    driver: 'Driver opted out',
    dispatcher: 'Bot paused',
    rollout: 'Bot not available',
}

const HINTS: Record<NonNullable<SmsConversationBot['off']> | 'on', string> = {
    on: "Answers this driver's ZIP check-ins with the loads that fit their truck",
    tenant: 'The load bot is switched off for the whole company',
    driver: 'This driver texted STOP — only they can undo it, with START',
    dispatcher: 'Paused — you took this conversation over. Tick to hand it back.',
    rollout: 'The load bot is not switched on for your company yet',
}

export const useConversationBot = (driverAccountId: string, surface: 'conversation_bar' | 'load_drawer'): ConversationBot => {
    const { showSnackbar } = useSnackbar()
    const [bot, setBot] = useState<SmsConversationBot | null>(null)
    const [saving, setSaving] = useState(false)
    /** Which conversation is on screen — every answer is checked against it before it is drawn. */
    const showing = useRef(driverAccountId)
    showing.current = driverAccountId

    const toggle = useCallback(
        async (on: boolean) => {
            setSaving(true)
            try {
                // The state the bot is actually in, never an echo of the request: a driver who texted STOP
                // stays off however this is flipped.
                const answer = await setConversationBot(driverAccountId, on)
                // `applied` compares the ask with what came back: a request to turn one on that answers
                // off is a driver's own STOP, not a failure, and the two must not read as one series.
                trackBoxTruckOfferBotToggled({ scope: 'conversation', surface, on, outcome: 'success', applied: answer.on === on })
                if (showing.current === driverAccountId) setBot(answer)
            } catch (error) {
                trackBoxTruckOfferBotToggled({ scope: 'conversation', surface, on, outcome: 'failure' })
                showSnackbar(SnackbarType.WARNING, getUserErrorMessage(error) || 'Could not change the bot for this conversation')
            } finally {
                setSaving(false)
            }
        },
        [driverAccountId, showSnackbar, surface]
    )

    const adopt = useCallback((next: SmsConversationBot | null | undefined, forDriver?: string) => {
        // The same guard `toggle` has, available to every caller: the bar's company-wide path re-read this
        // conversation after the save and adopted the answer blind.
        if (forDriver !== undefined && forDriver !== showing.current) return
        setBot(next ?? null)
    }, [])

    const off = bot && !bot.on ? bot.off : null
    return {
        bot,
        off,
        mine: !bot || bot.on || off === 'dispatcher',
        label: LABELS[off ?? 'on'],
        hint: HINTS[off ?? 'on'],
        saving,
        toggle,
        adopt,
    }
}
